import { HStack, PageContainer, PageHeader, Spread, Text, VStack } from '@/components/Atoms'
import { Card, CardContent } from '@/components/Card'
import { DataTable, DataTableRow } from '@/components/DataTable'
import { CustomerId } from '@/components/DataTable/CellContent/CustomerId'
import { CustomerNames } from '@/components/DataTable/CellContent/CustomerNames'
import NoDataAvailable from '@/components/DataTable/CellContent/NoDataAvailable'
import { TableHeader } from '@/components/DataTable/CellContent/TableHeader'
import { ExportCsvButton } from '@/components/ExportCsv/ExportCsvButton'
import PermissionsGuard from '@/components/Guard/PermissionsGuard'
import { Note } from '@/components/Note'
import { useCrtVersionSelectionSync } from '@/hooks/crt/useCrtVersionSelectionSync'
import usePermissions from '@/hooks/permissions/usePermissions'
import {
  budgetTrackerByKeyAccountExportQueryOptions,
  BudgetTrackerByKeyAccountSearch,
  budgetTrackerByKeyAccountSearchSchema,
  KeyAccountContract,
  useBudgetTrackerByKeyAccount,
} from '@/hooks/queries/crt/useBudgetTrackerByKeyAccount'
import { useKeyAccountContractRatios } from '@/hooks/queries/crt/useKeyAccountContractRatios'
import { useTablePagination } from '@/hooks/useTablePagination'

import { NO_DATA_AVAILABLE_SYMBOL } from '@/lib/utils/constants'
import { queryClient } from '@/main'
import { DATATABLE_NAMES } from '@/stores/datatable'
import { parseDateOnly } from '@/utils/DateOnlyHelpers'
import {
  CONTRACT_CLAUSES,
  ContractClause,
  INDETERMINATE,
  KEY_ACCOUNT_ESTIMATE_STATUS,
  KEY_ACCOUNT_ESTIMATE_STATUSES,
  KeyAccountEstimateStatus,
  PRODUCT_CATEGORIES,
  ProductCategory,
} from '@repo/constants'
import { hasPermissions } from '@repo/permissions'
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Separator,
} from '@repo/ui/components'
import { createFileRoute, Link } from '@tanstack/react-router'
import { ColumnDef, ColumnSort } from '@tanstack/react-table'
import { startOfYear } from 'date-fns'
import {
  AlertTriangleIcon,
  EllipsisVerticalIcon,
  ExternalLinkIcon,
  FilterIcon,
  PencilIcon,
  PlusIcon,
} from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toAssumptionBadges } from '../../-components/assumptions/assumption-selection'
import { CompactCurrencyCell } from '../-components/CompactCurrencyCell'
import { ConfigurationMenu } from './-components/ConfigurationMenu'
import { useOpenCrtConfigure } from '@/hooks/crt/useOpenCrtConfigure'
import { EditKeyAccountDialog } from './-components/EditKeyAccountDialog'
import { EstimatesCell } from './-components/EstimatesCell'

export const Route = createFileRoute('/_auth/_modules/crt/customer-investments/key-accounts/')({
  component: RouteComponent,
  validateSearch: (search) => budgetTrackerByKeyAccountSearchSchema.parse(search),
})

type KeyAccountContractRow = KeyAccountContract & DataTableRow

function RouteComponent() {
  const { t } = useTranslation(['crt', 'common'])
  const navigate = Route.useNavigate()
  const search = Route.useSearch()
  const { permissions } = usePermissions()
  const canEditEstimates = hasPermissions(permissions, 'crt_key_account_contract_estimates_edit')

  const [editTarget, setEditTarget] = useState<KeyAccountContract | null>(null)

  const { pageSize, onPageSizeChange } = useTablePagination({ navigate, search })

  const { crtPeriod: selectedCrtPeriodStr } = useCrtVersionSelectionSync({
    search,
    navigate: (search) => navigate({ search }),
    fields: ['manualInvestmentIds'],
  })
  const selectedCrtPeriodDate = parseDateOnly(selectedCrtPeriodStr)

  const [isConfigOpen, setIsConfigOpen] = useState(false)
  const configuration = { manualInvestmentIds: search.manualInvestmentIds }
  const configBadges = toAssumptionBadges(t, ['manual'], configuration)
  // The manual investments the page applies are changed from the header's configure dialog, which its
  // badges open; the page's own configuration menu keeps the Key Accounts tools.
  const openConfigure = useOpenCrtConfigure()
  // Drill-downs open on the same version and selection, rather than on the header's pinned one.
  const versionLinkSearch = {
    crtPeriod: selectedCrtPeriodStr,
    loadedVersionId: search.loadedVersionId,
    manualInvestmentIds: search.manualInvestmentIds,
  }

  // The loaded version only drives the selection mirror; the endpoint reads the ids themselves.
  const { loadedVersionId: _loadedVersionId, ...dataSearch } = search
  const { isPending, error, data } = useBudgetTrackerByKeyAccount({
    ...dataSearch,
    crtPeriod: selectedCrtPeriodStr,
    page: search.page ?? 1,
    pageSize,
  })
  const { getRatio } = useKeyAccountContractRatios(selectedCrtPeriodStr)

  // The breakdown tooltip mirrors the active filters: a selected category/clause collapses the
  // matrix to that single row/column.
  const visibleCategories = search.productCategory ? [search.productCategory] : PRODUCT_CATEGORIES
  const visibleClauses = search.contractClause ? [search.contractClause] : CONTRACT_CLAUSES

  const missingEstimatesCount = data?.missingEstimatesCount ?? 0
  const missingContractClauseCount = data?.missingContractClauseCount ?? 0
  const totalMasterAgreementCount = data?.totalMasterAgreementCount ?? 0
  const validThisYearMissingContractClauseCount = data?.validThisYearMissingContractClauseCount ?? 0
  const validThisYearMasterAgreementCount = data?.validThisYearMasterAgreementCount ?? 0

  const onPageChange = (page: number) => {
    navigate({ search: { ...search, page: page + 1 } })
  }

  const onSortChange = (sort: ColumnSort) => {
    navigate({ search: { ...search, sort } })
  }

  const handleProductCategoryChange = (next: string) => {
    navigate({
      search: {
        ...search,
        productCategory: next === INDETERMINATE ? undefined : (next as ProductCategory),
        page: 1,
      },
    })
  }

  const handleEstimateStatusChange = (next: string) => {
    navigate({
      search: {
        ...search,
        estimateStatus: next === INDETERMINATE ? undefined : (next as KeyAccountEstimateStatus),
        page: 1,
      },
    })
  }

  const handleContractClauseChange = (next: string) => {
    navigate({
      search: {
        ...search,
        contractClause: next === INDETERMINATE ? undefined : (next as ContractClause),
        page: 1,
      },
    })
  }

  const columns: ColumnDef<KeyAccountContractRow>[] = [
    {
      id: 'hierarchyErp',
      accessorFn: (row) => row.customer.hierarchyErp,
      header: ({ column }) => <TableHeader column={column} sortable />,
      cell: ({ row }) => <CustomerId id={row.original.customer.hierarchyErp} />,
      meta: { displayTitle: t('keyAccounts.table.hierarchyErp'), hideInColumnsMenu: true },
    },
    {
      id: 'accountType',
      accessorFn: (row) => row.customer.accountType,
      header: ({ column }) => <TableHeader column={column} sortable />,
      cell: ({ row }) =>
        row.original.customer.accountType ? (
          <Text>{row.original.customer.accountType}</Text>
        ) : (
          <NoDataAvailable />
        ),
      meta: { displayTitle: t('keyAccounts.table.accountType'), hideInColumnsMenu: true },
    },
    {
      id: 'name',
      accessorFn: (row) => row.customer.name,
      header: ({ column }) => <TableHeader column={column} sortable />,
      cell: ({ row }) => (
        <CustomerNames
          name={row.original.customer.name ?? NO_DATA_AVAILABLE_SYMBOL}
          legalName={row.original.customer.legalName ?? NO_DATA_AVAILABLE_SYMBOL}
        />
      ),
      meta: { displayTitle: t('keyAccounts.table.accountName'), hideInColumnsMenu: true },
    },
    {
      id: 'estimates',
      accessorFn: (row) => row.totals.estimates,
      header: ({ column }) => <TableHeader column={column} sortable align="right" />,
      cell: ({ row }) =>
        row.original.hasEstimate ? (
          <EstimatesCell
            row={row.original}
            getRatio={getRatio}
            categories={visibleCategories}
            clauses={visibleClauses}
          />
        ) : (
          <NoDataAvailable align="right" />
        ),
      meta: {
        displayTitle: t('currencyRates.cadColumn', { label: t('keyAccounts.table.estimates') }),
        cellClassName: 'text-right',
      },
    },
    {
      id: 'accruals',
      accessorFn: (row) => row.totals.accruals,
      header: ({ column }) => <TableHeader column={column} align="right" />,
      cell: ({ row }) => <CompactCurrencyCell value={row.original.totals.accruals} />,
      enableSorting: false,
      meta: { displayTitle: t('budgetTracker.table.actuals'), cellClassName: 'text-right' },
    },
    {
      id: 'forecast',
      accessorFn: (row) => row.totals.forecast,
      header: ({ column }) => <TableHeader column={column} align="right" />,
      cell: ({ row }) => <CompactCurrencyCell value={row.original.totals.forecast} />,
      enableSorting: false,
      meta: { displayTitle: t('budgetTracker.table.forecast'), cellClassName: 'text-right' },
    },
    {
      id: 'leftover',
      accessorFn: (row) => row.totals.leftover,
      header: ({ column }) => <TableHeader column={column} align="right" />,
      cell: ({ row }) => <CompactCurrencyCell value={row.original.totals.leftover} isSigned />,
      enableSorting: false,
      meta: { displayTitle: t('keyAccounts.table.leftover'), cellClassName: 'text-right' },
    },
    {
      id: 'details',
      header: () => null,
      cell: ({ row }) => {
        const hierarchyErp = row.original.customer.hierarchyErp
        return (
          <HStack className="justify-end">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  aria-label={t('keyAccounts.actions.viewDetails')}
                >
                  <EllipsisVerticalIcon className="text-muted-foreground size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {canEditEstimates && (
                  <>
                    <DropdownMenuItem
                      onClick={() => setEditTarget(row.original)}
                      className="cursor-pointer"
                    >
                      {row.original.hasEstimate ? (
                        <>
                          <PencilIcon className="size-4" />
                          {t('keyAccounts.actions.editEstimates')}
                        </>
                      ) : (
                        <>
                          <PlusIcon className="size-4" />
                          {t('keyAccounts.actions.addEstimate')}
                        </>
                      )}
                    </DropdownMenuItem>
                    <Separator />
                  </>
                )}
                <DropdownMenuItem
                  onClick={() =>
                    navigate({
                      to: '/crt/customer-investments/investments',
                      search: {
                        ...versionLinkSearch,
                        hierarchyErp: [hierarchyErp],
                        productCategory: search.productCategory,
                        contractClause: search.contractClause
                          ? [search.contractClause]
                          : [...CONTRACT_CLAUSES],
                      },
                    })
                  }
                  className="cursor-pointer"
                >
                  <ExternalLinkIcon className="size-4" />
                  {t('keyAccounts.actions.viewInvestments')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </HStack>
        )
      },
      enableSorting: false,
      meta: {
        headerClassName: 'w-16',
        cellClassName: 'w-16 text-right',
        hideInColumnsMenu: true,
      },
    },
  ]

  const productCategoryValue = search.productCategory ?? INDETERMINATE
  const contractClauseValue = search.contractClause ?? INDETERMINATE
  const estimateStatusValue = search.estimateStatus ?? INDETERMINATE

  // The estimates total stays in CAD like the estimates it sums, which are typed in CAD; actuals,
  // forecast and leftover are read-only and follow the display currency.
  const totals = data
    ? {
        estimates: <CompactCurrencyCell value={data.totals.estimates} isCad />,
        accruals: <CompactCurrencyCell value={data.totals.accruals} />,
        forecast: <CompactCurrencyCell value={data.totals.forecast} />,
        leftover: <CompactCurrencyCell value={data.totals.leftover} isSigned />,
      }
    : undefined

  return (
    <PageContainer>
      <Spread>
        <PageHeader>{t('keyAccounts.pageHeader')}</PageHeader>
        <HStack gap="sm">
          <ExportCsvButton
            getCsvExport={() =>
              queryClient.ensureQueryData(
                budgetTrackerByKeyAccountExportQueryOptions({
                  crtPeriod: selectedCrtPeriodStr,
                  productCategory: search.productCategory,
                  contractClause: search.contractClause,
                  estimateStatus: search.estimateStatus,
                  manualInvestmentIds: search.manualInvestmentIds,
                  sort: search.sort,
                }),
              )
            }
            rowCount={data?.totalCount ?? 0}
            label={t('export.viewData', { ns: 'common' })}
          />
        </HStack>
      </Spread>

      <VStack gap="sm">
        {!isPending && missingEstimatesCount > 0 && (
          <Note
            tone="warning"
            icon={AlertTriangleIcon}
            note={t('keyAccounts.notes.missingEstimates.banner', {
              count: missingEstimatesCount,
            })}
            action={
              <HStack gap="sm">
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() =>
                    handleEstimateStatusChange(KEY_ACCOUNT_ESTIMATE_STATUS.WITHOUT_ESTIMATE)
                  }
                >
                  <FilterIcon className="size-4" />
                  {t('keyAccounts.notes.missingEstimates.showAccounts')}
                </Button>
                <PermissionsGuard required={['crt_key_account_contract_estimates_edit']}>
                  <Button type="button" variant="secondary" size="sm" asChild>
                    <Link
                      to="/crt/customer-investments/key-accounts/estimates"
                      search={versionLinkSearch}
                    >
                      <ExternalLinkIcon className="size-4" />
                      {t('keyAccountsConfigurationMenu.estimates.editButton')}
                    </Link>
                  </Button>
                </PermissionsGuard>
              </HStack>
            }
          />
        )}

        {!isPending && validThisYearMissingContractClauseCount > 0 && (
          <Note
            tone="warning"
            icon={AlertTriangleIcon}
            note={t('keyAccounts.notes.missingContractClauseValidThisYear.banner', {
              count: validThisYearMissingContractClauseCount,
              total: validThisYearMasterAgreementCount,
            })}
            action={
              <PermissionsGuard required={['crt_agreement_mappings_edit']}>
                <Button type="button" variant="secondary" size="sm" asChild>
                  <Link
                    to="/crt/customer-investments/data-sources/master-agreements"
                    search={{ validFrom: startOfYear(selectedCrtPeriodDate) }}
                  >
                    <ExternalLinkIcon className="size-4" />
                    {t('keyAccounts.notes.missingContractClause.link')}
                  </Link>
                </Button>
              </PermissionsGuard>
            }
          />
        )}

        {!isPending && missingContractClauseCount > 0 && (
          <Note
            tone="warning"
            icon={AlertTriangleIcon}
            note={t('keyAccounts.notes.missingContractClause.banner', {
              count: missingContractClauseCount,
              total: totalMasterAgreementCount,
            })}
            action={
              <PermissionsGuard required={['crt_agreement_mappings_edit']}>
                <Button type="button" variant="secondary" size="sm" asChild>
                  <Link
                    to="/crt/customer-investments/data-sources/master-agreements"
                    search={{ crtPeriod: selectedCrtPeriodStr }}
                  >
                    <ExternalLinkIcon className="size-4" />
                    {t('keyAccounts.notes.missingContractClause.link')}
                  </Link>
                </Button>
              </PermissionsGuard>
            }
          />
        )}
      </VStack>

      <Card>
        <CardContent className="pt-6">
          <DataTable
            name={DATATABLE_NAMES.BUDGET_TRACKER_BY_KEY_ACCOUNTS}
            columns={columns}
            data={(data?.results ?? []) as KeyAccountContractRow[]}
            rowCount={data?.totalCount ?? 0}
            isPending={isPending}
            error={error}
            page={search.page}
            pageSize={pageSize}
            sort={search.sort}
            queryParams={search as BudgetTrackerByKeyAccountSearch}
            totals={totals}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
            onSortChange={onSortChange}
            actions={
              <>
                <Select value={productCategoryValue} onValueChange={handleProductCategoryChange}>
                  <SelectTrigger className="bg-background w-auto gap-2">
                    <div className="flex items-center gap-2">
                      <span className="text-muted-foreground">
                        {t('keyAccounts.filters.productCategory')}
                      </span>
                      <SelectValue />
                    </div>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={INDETERMINATE}>
                      {t('keyAccounts.filters.productCategoryOption.viewAll')}
                    </SelectItem>
                    {PRODUCT_CATEGORIES.map((category) => (
                      <SelectItem key={category} value={category}>
                        {t(`keyAccountContractEstimates.productCategory.${category}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={contractClauseValue} onValueChange={handleContractClauseChange}>
                  <SelectTrigger className="bg-background w-auto gap-2">
                    <div className="flex items-center gap-2">
                      <span className="text-muted-foreground">
                        {t('keyAccounts.filters.contractClause')}
                      </span>
                      <SelectValue />
                    </div>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={INDETERMINATE}>
                      {t('keyAccounts.filters.contractClauseOption.viewAll')}
                    </SelectItem>
                    {CONTRACT_CLAUSES.map((clause) => (
                      <SelectItem key={clause} value={clause}>
                        {t(`keyAccountContractEstimates.contractClause.${clause}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={estimateStatusValue} onValueChange={handleEstimateStatusChange}>
                  <SelectTrigger className="bg-background w-auto gap-2">
                    <div className="flex items-center gap-2">
                      <span className="text-muted-foreground">
                        {t('keyAccounts.filters.estimateStatus')}
                      </span>
                      <SelectValue />
                    </div>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={INDETERMINATE}>
                      {t('keyAccounts.filters.estimateStatusOption.viewAll')}
                    </SelectItem>
                    {KEY_ACCOUNT_ESTIMATE_STATUSES.map((status) => (
                      <SelectItem key={status} value={status}>
                        {t(`keyAccounts.filters.estimateStatusOption.${status}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </>
            }
            toolbarActions={
              <ConfigurationMenu
                isOpen={isConfigOpen}
                onOpenChange={setIsConfigOpen}
                crtPeriod={selectedCrtPeriodStr}
                linkSearch={versionLinkSearch}
              />
            }
            configBadges={configBadges}
            onConfigBadgeClick={openConfigure}
          />
        </CardContent>
      </Card>

      {editTarget && (
        <EditKeyAccountDialog
          open
          onOpenChange={(next) => !next && setEditTarget(null)}
          hierarchyErp={editTarget.customer.hierarchyErp}
          name={editTarget.customer.name}
          accountType={editTarget.customer.accountType}
          crtPeriod={selectedCrtPeriodStr}
          // An account without an estimate has nothing to delete yet; saving creates its estimates.
          withDeleteButton={editTarget.hasEstimate}
        />
      )}
    </PageContainer>
  )
}
